import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { checkOwnProperty, getChangedData, getDefaultWidgetName } from "utils/utils";
import parseWidgetData from "utils/widgetUtils";
import { GaugeWidgetFormStrings, InputErrorStrings, valueStrings, WidgetStrings } from "constants/constantStrings";
import { WIDGET_TITLE_INPUT_MAXIMUM } from "constants/globalConstants";
import { SYSTEMS_PAGES } from "constants/pages.constant";
import { WIDGET_TYPES } from "constants/widgets.type.const";

import CustomModal from "components/CustomModal";
import ErrorLabel from "components/ErrorLabel";
import InputBox from "components/InputBox";
import { TextFieldValidator } from "components/InputBox/TextFieldValidator";
import TagDropdown from "components/TagDropdown";

import GaugeFormConfig from "./gauge.form.config";

import "./index.scss";

export default function GaugeWidgetForm(props) {
  const [openModal, setOpenModal] = useState(true);
  const [isFormInvalid, setIsFormInvalid] = useState(true);
  const { Header, SubType, page, tagsList, currentLayout, isEdit, onWidgetAction, Attributes } = props.data;
  const { tagsAsObj } = useSelector(state => state.tags);
  const isReportView = () => page.toLowerCase() === SYSTEMS_PAGES.REPORTS;
  const GaugeData = Attributes ? parseWidgetData(props.data, tagsAsObj).GaugeData : null;
  const Tags = Attributes ? tagsAsObj[Attributes?.SystemTag[0]] : null;

  const getFormattedDetails = key => {
    let tag = null;
    const orgTag = GaugeData && GaugeData[key];
    if (orgTag) {
      const { TagId, TagName, Units, Value } = orgTag;
      tag = { TagId, TagName, Units, Value };
    }
    return tag;
  };

  const widgetState = {
    Header: Header || getDefaultWidgetName(),
    SubType: SubType || "LeftGauge",
    Attributes: {
      SystemTag: Tags,
      LL: getFormattedDetails("LL"),
      L: getFormattedDetails("L"),
      H: getFormattedDetails("H"),
      HH: getFormattedDetails("HH"),
      Comments: Attributes?.Comments || ""
    }
  };
  const [widgetData, setWidgetData] = useState(widgetState);

  useEffect(() => {
    setWidgetData(widgetState);
  }, [Header, SubType]);

  useEffect(
    () => () => {
      resetConfig();
    },
    []
  );

  const [gaugeThresholdConfig, setGaugeThresholdConfig] = useState(GaugeFormConfig.DEFAULT_THRESHOLD);

  const closeModal = closeType => {
    setOpenModal(false);
    const { onClose } = props;
    onClose(closeType);
  };

  const onTagChange = Tag => {
    const tempData = { ...widgetData, Attributes: { ...widgetData.Attributes, SystemTag: Tag } };
    validateForm(tempData);
  };

  const onTitleChange = Header => {
    const formData = { ...widgetData, Header };
    setWidgetData(formData);
    validateForm(formData);
  };
  const islimitValid = limit => limit && limit.Value?.toString();
  const validateForm = formData => {
    const { SubType, Header } = formData;
    const { L, LL, H, HH, SystemTag } = formData.Attributes;
    let isFormValid = SystemTag?.TagId && SubType && islimitValid(L) && islimitValid(LL) && islimitValid(H) && islimitValid(HH);
    if (isReportView()) {
      isFormValid = isFormValid && Header && Header.trim().length > 0;
    }
    setWidgetData(formData);
    setIsFormInvalid(!isFormValid);
  };

  const getDescription = () => (isReportView() ? GaugeWidgetFormStrings.reportDescription : GaugeWidgetFormStrings.dashboardDescription);

  const getEditTagPaylod = () => {
    const param = getChangedData(widgetData, widgetState);
    const { Attributes } = param;
    if (Attributes) {
      for (const key in Attributes) {
        if (typeof Attributes[key] === "object") {
          if (checkOwnProperty(Attributes[key], "TagId")) {
            Attributes[key] = key === WidgetStrings.systemTag ? [Attributes[key].TagId] : Attributes[key].TagId;
          } else Attributes[key] = Attributes[key].Value;
        }
      }
    }

    return { ...param, Id: props.data.Id, LayoutID: currentLayout.ID };
  };

  const addReportTag = async () => {
    let payload = {};
    if (isEdit) {
      payload = getEditTagPaylod();
    } else {
      payload = {
        ...getDashboardTagPayload()
      };
      onWidgetAction("ADD_WIDGET", payload);
    }
    closeModal(payload);
  };

  const getDashboardTagPayload = () => {
    const { Header, SubType, Attributes } = widgetData;
    const { LL, L, H, HH, Comments, SystemTag } = Attributes;
    return {
      Header,
      Type: WIDGET_TYPES.GAUGE,
      SubType: SubType,
      LayoutID: currentLayout.ID,
      Attributes: {
        SystemTag: new Array(SystemTag?.TagId),
        LL: LL.TagId || LL.Value,
        L: L.TagId || L.Value,
        H: H.TagId || H.Value,
        HH: HH.TagId || HH.Value,
        Comments: Comments || ""
      }
    };
  };

  const isLLThresholdInvalid = (val1, val2, val3, val4) =>
    val1 === val2 || val1 === val3 || val1 === val4 || val1 > val2 || val1 > val3 || val1 > val4;

  const isLThresholdInvalid = (val1, val2, val3, val4) =>
    val2 === val1 || val2 === val3 || val2 === val4 || val1 > val2 || val2 > val3 || val2 > val4;

  const isHThresholdInvalid = (val1, val2, val3, val4) =>
    val3 === val1 || val3 === val2 || val3 === val4 || val1 > val3 || val2 > val3 || val3 > val4;

  const isHHThresholdInvalid = (val1, val2, val3, val4) =>
    val4 === val1 || val4 === val2 || val4 === val3 || val1 > val4 || val2 > val4 || val3 > val4;

  const validateThresholdFields = () => {
    const { Attributes } = widgetData;
    const val1 = +Attributes.LL.Value;
    const val2 = +Attributes.L.Value;
    const val3 = +Attributes.H.Value;
    const val4 = +Attributes.HH.Value;
    const config = [...gaugeThresholdConfig];

    config[0].isInvalid = isLLThresholdInvalid(val1, val2, val3, val4);
    config[1].isInvalid = isLThresholdInvalid(val1, val2, val3, val4);
    config[2].isInvalid = isHThresholdInvalid(val1, val2, val3, val4);
    config[3].isInvalid = isHHThresholdInvalid(val1, val2, val3, val4);
    setGaugeThresholdConfig(config);

    return config[0].isInvalid || config[1].isInvalid || config[2].isInvalid || config[3].isInvalid;
  };

  const addDashboardTag = async () => {
    let payload = {};
    if (isEdit) {
      payload = getEditTagPaylod();
    } else {
      payload = getDashboardTagPayload();
      onWidgetAction("ADD_WIDGET", null, null, payload);
      setIsFormInvalid(false);
    }
    closeModal(payload);
  };

  const resetConfig = () => {
    const config = [...gaugeThresholdConfig];
    config[0].isInvalid = false;
    config[1].isInvalid = false;
    config[2].isInvalid = false;
    config[3].isInvalid = false;
    setGaugeThresholdConfig(config);
  };

  const onFormSubmit = () => {
    // Check if Threshold values are not invalid
    setIsFormInvalid(true);
    if (!validateThresholdFields()) {
      if (isReportView()) {
        addReportTag();
      } else {
        addDashboardTag();
      }
    }
  };

  const onGaugeTypeChange = event => {
    const SubType = event?.currentTarget?.getAttribute("Value");
    const tempData = { ...widgetData, SubType };
    validateForm(tempData);
  };

  const onThresholdTagChange = (id, value) => {
    const tempData = { ...widgetData, Attributes: { ...widgetData.Attributes, [id]: value } };
    validateForm(tempData);
  };

  const onThresholdInputChange = (id, Value) => {
    const data = { TagId: "", TagName: null, Value };
    const tempData = { ...widgetData, Attributes: { ...widgetData.Attributes, [id]: data } };
    validateForm(tempData);
  };

  const thresholdTagVal = id => {
    const data = widgetData.Attributes[id];
    return data?.TagId ? data : null;
  };

  const thresholdInputVal = id => {
    const data = widgetData.Attributes[id];
    return data?.TagName ? "" : data?.Value?.toString();
  };

  return (
    <>
      {openModal && (
        <CustomModal
          show={openModal}
          title={isEdit ? GaugeWidgetFormStrings.editTitle : GaugeWidgetFormStrings.addTitle}
          confirmBtnText={isEdit ? WidgetStrings.editWidget : WidgetStrings.addWidget}
          description={getDescription()}
          secondaryDescription={valueStrings.mandatoryField}
          close={closeModal}
          confirmBtnAction={onFormSubmit}
          confirmDisabled={isFormInvalid}
        >
          <div className="widget-form-container">
            {isReportView() && (
              <div className="form-group">
                <label className="bodyMainHeadingText" htmlFor="widgetTitle">
                  {WidgetStrings.widgetTitle}*
                </label>
                <InputBox
                  name="widgetTitle"
                  value={widgetData.Header}
                  placeholder={WidgetStrings.titlePlaceholder}
                  requiredMsg={InputErrorStrings.required}
                  onChange={onTitleChange}
                  validator={TextFieldValidator.ALPHANUMERIC_WITH_SPECIAL_CHARS}
                  maxLength={WIDGET_TITLE_INPUT_MAXIMUM}
                />
              </div>
            )}
            <div className="form-group">
              <label className="bodyMainHeadingText" htmlFor="tagDropdown">
                {WidgetStrings.selectTag}*
              </label>
              <TagDropdown
                id="tagDropdown"
                tagValue={widgetData.Attributes.SystemTag}
                requiredMsg={InputErrorStrings.required}
                dropDownID="addTagDropdown"
                tagList={tagsList}
                onTagChange={onTagChange}
              />
            </div>
            <div className="form-group">
              <label className="bodyMainHeadingText">{GaugeWidgetFormStrings.gaugeType}*</label>
              <div className="fullWidth flex-between marginTop10">
                {GaugeFormConfig.TYPES.map(gauge => (
                  <button
                    type="submit"
                    key={gauge.id}
                    id={gauge.id}
                    className={`gauge-type ${widgetData.SubType === gauge.type ? "gauge-type-selected" : null}`}
                    name={gauge.type}
                    value={gauge.type}
                    onClick={onGaugeTypeChange}
                  >
                    <gauge.icon />
                    <span>{gauge.text}</span>
                  </button>
                ))}
              </div>
            </div>
            {gaugeThresholdConfig.map(itemConf => (
              <div className="form-group" key={itemConf.label}>
                <label className="bodyMainHeadingText" htmlFor={itemConf.label}>
                  {itemConf.label}*
                </label>
                <div className="gauge-val-container" id={itemConf.label}>
                  <TagDropdown
                    tagValue={thresholdTagVal(itemConf.label)}
                    customClass="dropdown"
                    dropDownID={itemConf.label}
                    placeholder={GaugeWidgetFormStrings.selectTag}
                    tagList={tagsList}
                    onTagChange={val => onThresholdTagChange(itemConf.label, val)}
                  />
                  <span className="bodyMainHeadingText label"> {valueStrings.or} </span>
                  <InputBox
                    value={thresholdInputVal(itemConf.label)}
                    customClass="input-box"
                    name="widgetTitle"
                    validator={TextFieldValidator.NUMBER_WITH_DECIMAL}
                    placeholder={GaugeWidgetFormStrings.value}
                    onChange={val => onThresholdInputChange(itemConf.label, val)}
                  />
                </div>
                {itemConf.isInvalid && <ErrorLabel message={itemConf.error} />}
              </div>
            ))}
          </div>
        </CustomModal>
      )}
    </>
  );
}
